import axios, { AxiosError } from 'axios'

export const HTTP_CONTENT_TYPES = Object.freeze({
  CSV: 'text/csv; charset=UTF-8',
  FORM: 'application/x-www-form-urlencoded; charset=UTF-8',
  JSON: 'application/json; charset=UTF-8',
})

/** @param {unknown} error */
const errorInterceptor = error => {
  // Enhance errors that originate from an API request
  if (error instanceof AxiosError) {
    if (error.response) {
      error.data = error.response.data
      error.status = error.response.status
      error.requestedUrl = error.response.config.url

      // Update the error message to match the one returned from the API
      if (typeof error.data.error === 'string') {
        error.message = error.data.error
      } else if (typeof error.data.message === 'string') {
        error.message = error.data.message
      }
    } else if (error.request) {
      // Detect a connection timeout
      if (error.code === 'ECONNABORTED') {
        error.status = 408
        if (error.config) {
          error.message = `Timeout when performing ${error.config.method} on URL ${error.config.baseURL}${error.config.url}`
        }
      }
    }
  }
  throw error
}

/** @type {Map<string | Symbol, import('axios').AxiosInstance} */
const httpClients = new Map()

/**
 * @typedef HttpClientConfig The configuration required for creating HTTP clients.
 * @property {string} baseUrl The base URL for the HTTP client.
 * @property {import('vue').ComputedRef<string>} token A computed ref that returns the Bearer token to use for
 *   authenticating client API calls.
 * @property {string | null | undefined} [xApp] The X-App auditing header to use for all sent requests.
 */

/**
 * Creates a HTTP client under the given name using the provided configuration.
 *
 * @param {string | Symbol} clientKey The unique key to assign the created client. Used to fetch it later with
 *   {@link getHttpClient}.
 * @param {HttpClientConfig} config The configuration to use for the new client.
 */
export const initialiseHttpClient = (clientKey, { baseUrl, token, xApp }) => {
  const axiosInstance = axios.create({
    baseURL: baseUrl,
    // Set API timeout to 60 seconds, as no request should reasonably exceed this time
    timeout: 60 * 1000,
    paramsSerializer: {
      indexes: null,
    },
  })
  if (xApp) {
    axiosInstance.defaults.headers['X-App'] = xApp
  }

  // Axios request interceptor for adding authorization to requests and enhancing thrown request errors
  axiosInstance.interceptors.request.use(
    config => {
      if (token?.value && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token.value}`
      }
      return config
    },
    errorInterceptor,
    { synchronous: true },
  )

  // Axios response interceptor for returning response data directly and enhancing thrown response errors
  axiosInstance.interceptors.response.use(response => response.data, errorInterceptor)

  httpClients.set(clientKey, axiosInstance)
}

/**
 * Get a HTTP client by it's name.
 * @param {string | Symbol} clientKey The unique key of the client to get.
 * @returns The HTTP client instance.
 */
export const getHttpClient = clientKey => {
  if (!httpClients.has(clientKey)) {
    throw new Error(`HTTP Client for ${clientKey.toString()} has not been initialised`)
  }
  return httpClients.get(clientKey)
}
